import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { useGetAirlines } from './useAirlinesApi';
import { useSuppliersList } from '../suppliers/useSuppliersApi';

const BASE = '/flights/schedule-updater';

/**
 * Map an API flight segment to the UI shape.
 *
 * The backend returns one row per FLIGHT SEGMENT (a leg of one flight product in
 * one booking), not one row per booking — see docs/flight-schedule-updater.md §4.
 * `flightSegmentId` + `productId` are what the apply endpoint needs back.
 */
export const mapApiFlightToUi = (api) => {
  if (!api) return null;
  return {
    id: api.id,
    flightSegmentId: api.flightSegmentId ?? null,
    operationalFlightKey: api.operationalFlightKey ?? '',

    // Booking context
    bookingId: api.bookingId ?? null,
    bookingNumber: api.bookingNumber ?? '',
    customerName: api.customerName ?? '',
    productId: api.productId ?? null,
    bookingStatus: api.bookingStatus ?? '',
    productStatus: api.productStatus ?? '',
    // §69 — the version this row was read at; sent back on apply as the race guard.
    productUpdatedAt: api.productUpdatedAt ?? null,

    // Supplier / reference
    supplier: api.supplier ?? '',
    supplierReference: api.supplierReference ?? '',
    pnr: api.pnr ?? '',
    source: api.source ?? '',

    // Flight identity
    airline: api.airline ?? '',
    airlineCode: api.airlineCode ?? '',
    airlineLogo: api.airlineLogo ?? '',
    flightNumber: api.flightNumber ?? '',
    flightType: api.flightType ?? '',
    direction: api.direction ?? '',

    // Schedule
    route: api.route ?? '',
    departureAirport: api.departureAirport ?? '',
    departureCity: api.departureCity ?? '',
    arrivalAirport: api.arrivalAirport ?? '',
    arrivalCity: api.arrivalCity ?? '',
    departureDate: api.departureDate ?? '',
    departureTime: api.departureTime ?? '',
    arrivalDate: api.arrivalDate ?? '',
    arrivalTime: api.arrivalTime ?? '',

    cabinClass: api.cabinClass ?? '',
    fareClass: api.fareClass ?? '',
    flightStatus: api.flightStatus ?? '',
    cancelled: Boolean(api.cancelled),

    // Impact scope (§12) — counted across ALL bookings on the same operational
    // flight, not just the ones in the current filtered view.
    travelers: api.travelers ?? 0,
    affectedBookings: api.affectedBookings ?? 1,
    affectedTravelers: api.affectedTravelers ?? 0,

    // Schedule-change state (§52)
    scheduleStatus: api.scheduleStatus ?? 'UNCHECKED',
    hasScheduleChange: Boolean(api.hasScheduleChange),
    lastChange: api.lastChange ?? null,
    lastCheckedAt: api.lastCheckedAt ?? null,
  };
};

/**
 * useGetAirlinesForScheduleUpdater - fetch airlines for filter dropdown
 */
export const useGetAirlinesForScheduleUpdater = () => {
  return useGetAirlines({ page: 1, limit: 500, status: '' });
};

/**
 * useGetSuppliersForScheduleUpdater - fetch suppliers for filter dropdown
 */
export const useGetSuppliersForScheduleUpdater = () => {
  return useSuppliersList({});
};

/**
 * useSearchFlights - segment-level flight search over real booking data.
 *
 * There is deliberately NO client-side fallback here. An error must surface as
 * an error: showing stand-in flights when the API fails would put fabricated
 * schedules in front of an operator about to act on them.
 *
 * @returns { data, summary, loading, error, search }
 */
export const useSearchFlights = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = useCallback(async (appliedFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (appliedFilters?.dateFrom) params.dateFrom = appliedFilters.dateFrom;
      if (appliedFilters?.dateTo) params.dateTo = appliedFilters.dateTo;
      if (appliedFilters?.supplier && appliedFilters.supplier !== 'all') params.supplier = appliedFilters.supplier;
      if (appliedFilters?.airline && appliedFilters.airline !== 'all') params.airline = appliedFilters.airline;
      if (appliedFilters?.flightNumber) params.flightNumber = appliedFilters.flightNumber;
      if (appliedFilters?.route) params.route = appliedFilters.route;
      if (appliedFilters?.bookingStatus && appliedFilters.bookingStatus !== 'all') params.bookingStatus = appliedFilters.bookingStatus;
      if (appliedFilters?.bookingNumber) params.bookingNumber = appliedFilters.bookingNumber;
      if (appliedFilters?.pnr) params.pnr = appliedFilters.pnr;

      const response = await axiosInstance.get(`${BASE}/flights`, { params });
      const res = response.data;
      if (res.success && Array.isArray(res.data)) {
        setData(res.data.map(mapApiFlightToUi));
        setSummary(res.summary ?? null);
      } else {
        setData([]);
        setSummary(null);
        setError(res.message || 'Failed to load flights');
      }
    } catch (err) {
      setData([]);
      setSummary(null);
      setError(err.response?.data?.message || err.message || 'Failed to search flights');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, summary, loading, error, search };
};

/**
 * useCheckScheduleApi - ask the supplier API whether a schedule has changed.
 *
 * SUNSKY has no supplier schedule-check integration yet, so this endpoint
 * currently answers 501 / API_ERROR by design (spec §21: an API failure must
 * never be reported as "no change"). The server's message is surfaced verbatim
 * rather than being softened here.
 */
/**
 * useSupplierSchedule
 *
 * What Airtuerk states for the segments being edited, so the Manual Schedule
 * Change form can offer the supplier's own figures rather than a copy of the
 * booking's current ones. Returns segments keyed by flightSegmentId.
 */
export const useSupplierSchedule = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (productIds) => {
    const ids = [...new Set((productIds || []).filter(Boolean))];
    if (!ids.length) {
      setData(null);
      return null;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${BASE}/supplier-schedule`, { productIds: ids });
      const payload = {
        segments: response.data?.data?.segments || {},
        notFound: response.data?.data?.notFound || [],
        message: response.data?.message || '',
        success: Boolean(response.data?.success),
      };
      setData(payload);
      return payload;
    } catch (err) {
      // A supplier lookup that fails must not block the form — it falls back to
      // manual entry, and says so.
      const payload = {
        segments: {},
        notFound: [],
        message: err.response?.data?.message || err.message || 'Could not reach Airtuerk',
        success: false,
      };
      setData(payload);
      return payload;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => setData(null), []);

  return { data, loading, execute, reset };
};

export const useCheckScheduleApi = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${BASE}/check`);
      const res = response.data;
      return {
        success: Boolean(res.success),
        message: res.message || (res.success ? 'Schedule check completed' : 'Schedule check failed'),
        classification: res.classification || null,
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to check schedule',
        classification: err.response?.data?.classification || 'API_ERROR',
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
};

/**
 * useApplyScheduleChanges - apply a manual schedule change to selected segments.
 *
 * @param {Array}  segments    [{ productId, flightSegmentId }] — the rows to change
 * @param {Object} newSchedule { departureDate, departureTime, arrivalDate, arrivalTime, ... }
 * @param {Object} options     { reason (required), detectedVia, impact, notes, allowInvalidConnection }
 *
 * Every booking is processed individually server-side, so the result reports per
 * booking (§70) — read `data.results`, not just the top-level message.
 */
export const useApplyScheduleChanges = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (segments, newSchedule, options = {}) => {
    if (!segments || segments.length === 0) {
      return { success: false, message: 'No flights selected' };
    }
    if (!options.reason || !String(options.reason).trim()) {
      return { success: false, message: 'A reason is required for a manual schedule change' };
    }
    setLoading(true);
    try {
      // §69 — send the version each segment was READ at. If someone else changed
      // the product since this screen loaded, the server refuses that booking
      // rather than overwriting work the operator never saw.
      const expectedVersions = {};
      segments.forEach((s) => {
        if (s.productId && s.productUpdatedAt) {
          expectedVersions[String(s.productId)] = s.productUpdatedAt;
        }
      });

      const response = await axiosInstance.post(`${BASE}/apply`, {
        segments: segments.map((s) => ({
          productId: s.productId,
          flightSegmentId: s.flightSegmentId,
        })),
        expectedVersions,
        newSchedule,
        reason: options.reason,
        detectedVia: options.detectedVia || 'Manual',
        impact: options.impact || 'Minor',
        notes: options.notes,
        allowInvalidConnection: Boolean(options.allowInvalidConnection),
        // §28 — linked transfers are only modified when the operator says so.
        syncTransfers: options.syncTransfers !== false,
        // §43 — reissue vouchers carrying the old schedule.
        regenerateDocuments: options.regenerateDocuments !== false,
      });
      const res = response.data;
      return {
        success: Boolean(res.success),
        message: res.message || 'Changes applied',
        data: res.data || null,
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to apply changes',
        data: err.response?.data?.data || null,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
};

/**
 * useImpactPreview - dry run of what a change would do to the rest of each
 * booking (§25). Read-only; nothing is written.
 *
 * §13 requires the operator to preview impact BEFORE confirming — an answer that
 * only arrives with the result is not a preview.
 */
export const useImpactPreview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (segments, newSchedule) => {
    if (!segments?.length || !newSchedule || !Object.keys(newSchedule).length) {
      setData(null);
      return null;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${BASE}/impact`, {
        segments: segments.map((s) => ({
          productId: s.productId,
          flightSegmentId: s.flightSegmentId,
        })),
        newSchedule,
      });
      const res = response.data;
      const next = res.success ? res.data : null;
      setData(next);
      return next;
    } catch {
      setData(null); // the preview is advisory — never block the change on it
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, execute };
};

/**
 * useNotifyCustomer - tell the lead booker their flight moved (§47).
 *
 * Sending is what completes a schedule change: once the documents are reissued
 * AND the customer is told, the change resolves and the flight stops being
 * flagged (§76). `resolved` in the response says whether that happened.
 *
 * A repeat send is refused with 409 unless `resend` is passed (§68).
 */
export const useNotifyCustomer = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (scheduleChangeId, { resend = false } = {}) => {
    if (!scheduleChangeId) {
      return { success: false, message: 'This flight has no recorded schedule change to notify about.' };
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${BASE}/notify`, { scheduleChangeId, resend });
      const res = response.data;
      return {
        success: Boolean(res.success),
        message: res.message,
        resolved: Boolean(res.resolved),
        status: res.status,
      };
    } catch (err) {
      const data = err.response?.data;
      return {
        success: false,
        alreadyNotified: Boolean(data?.alreadyNotified),
        message: data?.message || err.message || 'Failed to notify the customer',
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
};

/**
 * useScheduleDashboard - operational counters (§62).
 *
 * A counter of `null` means "no subsystem behind it", NOT zero — render it as
 * unavailable. Showing "0 API errors" when nothing was ever checked is the same
 * false reassurance §21 forbids.
 */
export const useScheduleDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${BASE}/dashboard`);
      if (response.data?.success) setData(response.data.data);
      else setData(null);
    } catch {
      setData(null); // counters are supplementary — never block the screen
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, load };
};

/**
 * useFlightsOnOperationalFlight - every booking sitting on one operational
 * flight (§12, §66) — "find all bookings on this flight".
 */
export const useFlightsOnOperationalFlight = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (operationalFlightKey) => {
    if (!operationalFlightKey) return { success: false, message: 'No flight selected' };
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${BASE}/operational-flights/${encodeURIComponent(operationalFlightKey)}/bookings`
      );
      const res = response.data;
      return { success: Boolean(res.success), data: res.data || null, message: res.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to load bookings for this flight',
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
};
